import { User, GlobalConfig } from '../../types';
import { hasUnlimitedTokens, getTokenMultiplier } from '../../utils/promoEffectsManager';
import {
    getEffectiveTokenLimit,
    getTokenUsagePercentage,
    getTokenStatusText,
    shouldShowUpgradePrompt,
    canSendMessage,
    getTokenRefreshAmount as getTokenRefreshAmountFromUtils
} from '../../utils/tokenManager';
import { getTokenConfig } from './config';
import { TokenChargeRequest, calculateCharge } from './pricing';

export interface TokenStatus {
    remaining: number | 'unlimited';
    limit: number | 'unlimited';
    usagePercent: number;
    statusText: string;
    hasUnlimited: boolean;
    multiplier: number;
    canSend: boolean;
    shouldShowUpgrade: boolean;
}

/**
 * Get comprehensive token status for a user
 */
export const getStatus = (user: User | null, globalConfig: GlobalConfig): TokenStatus => {
    const limit = getEffectiveTokenLimit(user, globalConfig);
    const hasUnlimited = hasUnlimitedTokens(user);
    const currentUsage = user?.dailyTokenUsage || 0;

    return {
        remaining: hasUnlimited ? 'unlimited' : (typeof limit === 'number' ? limit - currentUsage : 'unlimited'),
        limit,
        usagePercent: getTokenUsagePercentage(user, globalConfig),
        statusText: getTokenStatusText(user, globalConfig),
        hasUnlimited,
        multiplier: getTokenMultiplier(user),
        canSend: canSend(user, globalConfig, 100).canSend, // Basic check with 100 tokens
        shouldShowUpgrade: shouldShowUpgradePrompt(user, globalConfig)
    };
};

/**
 * Check if user can send a message with given token cost
 */
export const canSend = (
    user: User | null,
    globalConfig: GlobalConfig,
    estimatedCost: number
): { canSend: boolean; reason?: string } => {
    return canSendMessage(user, globalConfig, estimatedCost);
};

/**
 * Check if modal should be shown for this request
 */
export const shouldShowWarningModal = (request: TokenChargeRequest, user: User): boolean => {
    const result = calculateCharge(request, user);

    // Always show if cannot proceed
    if (!result.canProceed) {
        return true;
    }

    // Check user preferences
    try {
        const prefs = JSON.parse(localStorage.getItem('token_modal_preferences') || '{}');

        if (prefs.neverShow) return false;
        if (prefs.alwaysShow) return true;
        if (prefs.dontShowForComplexity?.includes(request.complexity)) return false;

        const threshold = prefs.thresholdTokens || 300;
        return result.charged >= threshold;
    } catch (error) {
        // Default: show for costs >= 300 tokens
        return result.charged >= 300;
    }
};

/**
 * Get token refresh amount per cycle
 */
export const getTokenRefreshAmount = (user: User | null, globalConfig: GlobalConfig): number => {
    return getTokenRefreshAmountFromUtils(user, globalConfig);
};
